export const SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT = 5;
export const SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT = false;

export const SchemaBrowserType = {
  INPUT: 'INPUT',
  OUTPUT: 'OUTPUT',
  ADD_ATTRS: 'ADD_ATTRS',
  SCHEMA_SPEC: 'SCHEMA_SPEC',
  NONE: 'NONE',
} as const;

export type SchemaBrowserTypeUnion = 'INPUT' | 'OUTPUT' | 'ADD_ATTRS' | 'SCHEMA_SPEC' | 'NONE';

export const SchemaEditorViewModeType = {
  TABLE: 'TABLE',
  TABLE_TRANSPOSE: 'TABLE_TRANSPOSE',
  CODE: 'CODE',
};

export const TransformTypes = {
  TRANSFORM_NONE: 'TRANSFORM_NONE',
  TRANSFORM_NOOP: 'eqAttr',
};

export const FilterTypesKeys = {
  FILTER_NONE: 'FILTER_NONE',
  FILTER_EQ: 'FILTER_EQ',
  FILTER_NEQ: 'FILTER_NEQ',
  FILTER_GT: 'FILTER_GT',
  FILTER_LT: 'FILTER_LT',
  FILTER_NOT_NULL: 'FILTER_NOT_NULL',
  FILTER_IS_NULL: 'FILTER_IS_NULL',
  FILTER_ARR_CONTAINS: 'FILTER_ARR_CONTAINS',
  FILTER_ARR_DOESNT_CONTAIN: 'FILTER_ARR_DOESNT_CONTAIN',
  FILTER_IS_PRESENT: 'FILTER_IS_PRESENT',
  FILTER_IS_NOT_PRESENT: 'FILTER_IS_NOT_PRESENT',
  FILTER_REGEX_CONTAINS: 'FILTER_REGEX_CONTAINS',
  FILTER_REGEX_BEGINS: 'FILTER_REGEX_BEGINS',
  FILTER_REGEX_ENDS: 'FILTER_REGEX_ENDS',
} as const;

export type FilterRuleOp =
  | 'FILTER_NONE'
  | 'FILTER_EQ'
  | 'FILTER_NEQ'
  | 'FILTER_GT'
  | 'FILTER_LT'
  | 'FILTER_NOT_NULL'
  | 'FILTER_IS_NULL'
  | 'FILTER_ARR_CONTAINS'
  | 'FILTER_ARR_DOESNT_CONTAIN'
  | 'FILTER_IS_PRESENT'
  | 'FILTER_IS_NOT_PRESENT'
  | 'FILTER_REGEX_CONTAINS'
  | 'FILTER_REGEX_BEGINS'
  | 'FILTER_REGEX_ENDS';

export const FilterTypes = {
  [FilterTypesKeys.FILTER_NONE]: {
    idx: 0,
    label: '',
    value: FilterTypesKeys.FILTER_NONE,
    op: '',
    needsVal: false,
  },
  [FilterTypesKeys.FILTER_EQ]: {
    idx: 1,
    label: 'Equal to',
    value: FilterTypesKeys.FILTER_EQ,
    op: '==',
    needsVal: true,
  },
  [FilterTypesKeys.FILTER_NEQ]: {
    idx: 2,
    label: 'Not equal to',
    value: FilterTypesKeys.FILTER_NEQ,
    op: '!=',
    needsVal: true,
  },
  [FilterTypesKeys.FILTER_GT]: {
    idx: 3,
    label: 'Greater than',
    value: FilterTypesKeys.FILTER_GT,
    op: '>',
    needsVal: true,
  },
  [FilterTypesKeys.FILTER_LT]: {
    idx: 4,
    label: 'Less than',
    value: FilterTypesKeys.FILTER_LT,
    op: '<',
    needsVal: true,
  },
  [FilterTypesKeys.FILTER_NOT_NULL]: {
    idx: 5,
    label: 'Is Not Null',
    value: FilterTypesKeys.FILTER_NOT_NULL,
    op: '!=',
    needsVal: false,
  },
  [FilterTypesKeys.FILTER_IS_NULL]: {
    idx: 6,
    label: 'Is Null',
    value: FilterTypesKeys.FILTER_IS_NULL,
    op: '==',
    needsVal: false,
  },
  [FilterTypesKeys.FILTER_ARR_CONTAINS]: {
    idx: 7,
    label: 'Array Contains Value',
    value: FilterTypesKeys.FILTER_ARR_CONTAINS,
    op: 'in',
    needsVal: true,
  },
  [FilterTypesKeys.FILTER_ARR_DOESNT_CONTAIN]: {
    idx: 8,
    label: "Array Doesn't Contain Value",
    value: FilterTypesKeys.FILTER_ARR_DOESNT_CONTAIN,
    op: 'nin',
    needsVal: true,
  },
  [FilterTypesKeys.FILTER_IS_PRESENT]: {
    idx: 9,
    label: 'Is Present in Record',
    value: FilterTypesKeys.FILTER_IS_PRESENT,
    op: '@',
    needsVal: false,
  },
  [FilterTypesKeys.FILTER_IS_NOT_PRESENT]: {
    idx: 10,
    label: 'Is Not Present in Record',
    value: FilterTypesKeys.FILTER_IS_NOT_PRESENT,
    op: '!@',
    needsVal: false,
  },
  [FilterTypesKeys.FILTER_REGEX_CONTAINS]: {
    idx: 11,
    label: 'Contains',
    value: FilterTypesKeys.FILTER_REGEX_CONTAINS,
    op: '=~',
    needsVal: true,
  },
  [FilterTypesKeys.FILTER_REGEX_BEGINS]: {
    idx: 12,
    label: 'Begins With',
    value: FilterTypesKeys.FILTER_REGEX_BEGINS,
    op: '=~',
    needsVal: true,
  },
  [FilterTypesKeys.FILTER_REGEX_ENDS]: {
    idx: 13,
    label: 'Ends With',
    value: FilterTypesKeys.FILTER_REGEX_ENDS,
    op: '=~',
    needsVal: true,
  },
};

export const FilterOperators = {
  AND: '&&',
  OR: '||',
} as const;

export type FilterJoinOp = '&&' | '||';

export const FilterValueTypeOptions = [
  { label: 'String', value: 'string', defaultValue: '' },
  { label: 'Number', value: 'number', defaultValue: 0 },
  { label: 'Boolean', value: 'boolValue', defaultValue: false },
];

export const boolValueOptions = [
  {
    label: 'False',
    value: 'false',
  },
  {
    label: 'True',
    value: 'true',
  },
];

export const TransformParameterTypes = {
  FILTER_GROUP: 'filterGroup',
  FILTER_RULE: 'filterRule',
  FILTER_RULE_JOIN_OP: 'ruleJoinOp',
  SHARE: 'share',
  FUNCTION: 'function',
  ATTR_REF: 'attributeRef',
  SYS_ATTR_REF: 'systemAttributeRef',
  STRING_INP: 'string',
  NUMBER_INP: 'number',
  ARRAY: 'array',
  DEFAULTS: 'defaults',
} as const;

interface FilterRuleParameter {
  type: 'filterRule';
  ruleOp: FilterRuleOp;
  ruleVal: {
    type: string;
    value: string | null;
  };
  ruleAttribute?: string;
}

interface FilterJoinParameter {
  type: 'ruleJoinOp';
  value: FilterJoinOp;
}

export type FilterParameter = FilterRuleParameter | FilterJoinParameter;

export const TRANSFORM_RULES_PROTO_STR = {
  type: TransformParameterTypes.STRING_INP,
  value: null,
};

export const TRANSFORM_RULES_PROTO_NUM = {
  type: TransformParameterTypes.NUMBER_INP,
  value: null,
};

export const TRANSFORM_RULES_PROTO_ATTR = {
  type: TransformParameterTypes.ATTR_REF,
  name: null,
};

export const TRANSFORM_RULES_PROTO_SYS_ATTR = {
  type: TransformParameterTypes.SYS_ATTR_REF,
  name: null,
};

export const TRANSFORM_RULES_PROTO_FUNC = {
  type: TransformParameterTypes.FUNCTION,
  name: TransformTypes.TRANSFORM_NONE,
};

export const DefaultsTypes = {
  DEFAULT_NONE: 'DEFAULT_NONE',
  DEFAULT_NULL: 'DEFAULT_NULL',
  DEFAULT_STR: 'DEFAULT_STR',
  DEFAULT_NUM: 'DEFAULT_NUM',
};

export const DefaultsViewConstants = {
  [DefaultsTypes.DEFAULT_NONE]: {
    idx: 0,
    label: 'No Default',
    value: DefaultsTypes.DEFAULT_NONE,
    needsVal: false,
  },
  [DefaultsTypes.DEFAULT_NULL]: {
    idx: 1,
    label: 'Null',
    value: DefaultsTypes.DEFAULT_NULL,
    needsVal: false,
  },
  [DefaultsTypes.DEFAULT_STR]: {
    idx: 2,
    label: 'String',
    value: DefaultsTypes.DEFAULT_STR,
    needsVal: true,
  },
  [DefaultsTypes.DEFAULT_NUM]: {
    idx: 3,
    label: 'Number',
    value: DefaultsTypes.DEFAULT_NUM,
    needsVal: true,
  },
};

export const ValidationTypes = {
  REQUIRED: 'REQUIRED',
  FORMAT: 'FORMAT',
  RANGE: 'RANGE',
  ENUM: 'ENUM',
  PATTERN: 'PATTERN',
  SIZE: 'SIZE',
};

export const ValidationTypeOptions = [
  {
    label: 'Required',
    value: ValidationTypes.REQUIRED,
  },
  {
    label: 'Data Type',
    value: ValidationTypes.FORMAT,
  },
  {
    label: 'Range',
    value: ValidationTypes.RANGE,
  },
  {
    label: 'Enum',
    value: ValidationTypes.ENUM,
  },
  {
    label: 'Pattern',
    value: ValidationTypes.PATTERN,
  },
  {
    label: 'Size',
    value: ValidationTypes.SIZE,
  },
];

export const ValidationHeadings = {
  [ValidationTypes.REQUIRED]: 'Required Attributes',
  [ValidationTypes.FORMAT]: 'Data Type Validation',
  [ValidationTypes.RANGE]: 'Range Validation',
  [ValidationTypes.ENUM]: 'Enum Validation',
  [ValidationTypes.PATTERN]: 'Pattern Validation',
  [ValidationTypes.SIZE]: 'Size Validation',
};

export const TransformTabTypes = {
  TAB_AUTO_MAPPING: 'TAB_AUTO_MAPPING',
  TAB_TRANSFORM_BUILDER: 'TAB_TRANSFORM_BUILDER',
  TAB_CUSTOM_CODE: 'TAB_CUSTOM_CODE',
};

export const TransformTabConstants = {
  codeEditorMode: {
    NONE: 'NONE',
    CUSTOM_CODE: 'CUSTOM_CODE',
    REUSABLE_TX: 'REUSABLE_TX',
  },
  tabOrder: [
    TransformTabTypes.TAB_AUTO_MAPPING,
    TransformTabTypes.TAB_TRANSFORM_BUILDER,
    TransformTabTypes.TAB_CUSTOM_CODE,
  ],
  tabInfo: {
    [TransformTabTypes.TAB_AUTO_MAPPING]: {
      name: 'Replicate',
      index: '0',
    },
    [TransformTabTypes.TAB_TRANSFORM_BUILDER]: {
      name: 'Transform Builder',
      index: '1',
    },
    [TransformTabTypes.TAB_CUSTOM_CODE]: {
      name: 'Custom Code',
      index: '2',
    },
  },
};

export const InspectorTabTypes = {
  TAB_TRANSFORMS: 'TAB_TRANSFORMS',
  TAB_VALIDATIONS: 'TAB_VALIDATIONS',
};

export const InspectorTabConstants = {
  tabOrder: [InspectorTabTypes.TAB_TRANSFORMS, InspectorTabTypes.TAB_VALIDATIONS],
  tabInfo: {
    [InspectorTabTypes.TAB_TRANSFORMS]: {
      name: 'Transforms',
      index: 0,
    },
    [InspectorTabTypes.TAB_VALIDATIONS]: {
      name: 'Output Validations',
      index: 1,
    },
  },
};

export const SchemaEditorSampleError = {
  NOT_LOADED: 'NOT_LOADED',
  NULL_VALUE: 'NULL_VALUE',
  MISSING_VALUE: 'MISSING_VALUE',
  UNDEFINED_VALUE: 'UNDEFINED_VALUE',
  FILTERED_OUT: 'FILTERED_OUT',
  FILLER_CELL: 'FILLER_CELL',
  MISSING_TRANSFORM: 'MISSING_TRANSFORM',
};

export const RECIPE_GROUP_TRANSFORMS = 'tx_builder';
export const RECIPE_GROUP_TRANSFORMS_CODE = 'tx_code';
export const RECIPE_GROUP_TRANSFORMS_AGGREGATE = 'tx_aggregate';
export const RECIPE_GROUP_TRANSFORMS_REPLICATE = 'tx_replicate';
export const RECIPE_GROUP_DEFAULTS = 'defaults';
export const RECIPE_GROUP_FILTERS = 'filters';
export const RECIPE_GROUP_VALIDATIONS = 'validations';
export const RECIPE_GROUP_ANNOTATIONS = 'annotations';

export type RecipeGroupType =
  | 'tx_builder'
  | 'tx_code'
  | 'tx_aggregate'
  | 'tx_replicate'
  | 'defaults'
  | 'filters'
  | 'validations'
  | 'annotations';

export const RECIPE_GROUP_LABELS = {
  [RECIPE_GROUP_TRANSFORMS]: 'Transform',
  [RECIPE_GROUP_TRANSFORMS_CODE]: 'Transform: Code',
  [RECIPE_GROUP_TRANSFORMS_AGGREGATE]: 'Transform: Aggregate',
  [RECIPE_GROUP_TRANSFORMS_REPLICATE]: 'Transform: Replicate',
  [RECIPE_GROUP_DEFAULTS]: 'Default Values',
  [RECIPE_GROUP_FILTERS]: 'Record Filters',
  [RECIPE_GROUP_VALIDATIONS]: 'Validations',
  [RECIPE_GROUP_ANNOTATIONS]: 'Annotations',
};

export const RECIPE_GROUP_DESCRIPTIONS = {
  [RECIPE_GROUP_TRANSFORMS]: 'Transform data using the Transform Builder',
  [RECIPE_GROUP_TRANSFORMS_CODE]: 'Write custom code or reuse transforms',
  [RECIPE_GROUP_TRANSFORMS_AGGREGATE]: 'Combine data from multiple records',
  [RECIPE_GROUP_TRANSFORMS_REPLICATE]: 'Move all data to the output without modifications',
  [RECIPE_GROUP_DEFAULTS]: 'Set default values for attributes',
  [RECIPE_GROUP_FILTERS]: 'Filter records based on attribute values',
  [RECIPE_GROUP_VALIDATIONS]: 'Validate attribute values',
  [RECIPE_GROUP_ANNOTATIONS]: 'Set output attribute descriptions',
};

export const RECIPE_GROUP_ORDER = [
  RECIPE_GROUP_TRANSFORMS,
  RECIPE_GROUP_TRANSFORMS_CODE,
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_REPLICATE,
  RECIPE_GROUP_DEFAULTS,
  RECIPE_GROUP_FILTERS,
  RECIPE_GROUP_VALIDATIONS,
  RECIPE_GROUP_ANNOTATIONS,
];

export const TX_BUILDER_RECIPE_GROUPS = [
  RECIPE_GROUP_TRANSFORMS,
  RECIPE_GROUP_FILTERS,
  RECIPE_GROUP_DEFAULTS,
];
